/**
 * PaisesCampaignHub — Envíos masivos por país con banderas
 * Selecciona uno o más países y lanza una campaña WhatsApp/Email al CRM filtrado.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Send, RefreshCw, Globe, Users, MessageCircle, Mail, CheckCircle2, X, Zap } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAISES = [
  { code: 'ES', flag: '🇪🇸', name: 'España',          tag: 'españa' },
  { code: 'PE', flag: '🇵🇪', name: 'Perú',            tag: 'peru' },
  { code: 'MX', flag: '🇲🇽', name: 'México',          tag: 'mexico' },
  { code: 'CL', flag: '🇨🇱', name: 'Chile',           tag: 'chile' },
  { code: 'CO', flag: '🇨🇴', name: 'Colombia',        tag: 'colombia' },
  { code: 'AR', flag: '🇦🇷', name: 'Argentina',       tag: 'argentina' },
  { code: 'EC', flag: '🇪🇨', name: 'Ecuador',         tag: 'ecuador' },
  { code: 'BO', flag: '🇧🇴', name: 'Bolivia',         tag: 'bolivia' },
  { code: 'DO', flag: '🇩🇴', name: 'R. Dominicana',   tag: 'republica dominicana' },
  { code: 'CR', flag: '🇨🇷', name: 'Costa Rica',      tag: 'costa rica' },
  { code: 'US', flag: '🇺🇸', name: 'EEUU',            tag: 'eeuu' },
  { code: 'OT', flag: '🌍', name: 'Otros Países',      tag: 'otros' },
];

const NICHOS = [
  { id: 'todos',       label: 'Todos los nichos',    tag: '' },
  { id: 'dental',      label: 'Clínicas Dentales',   tag: 'nicho:dental' },
  { id: 'contable',    label: 'Estudios Contables',  tag: 'nicho:contable' },
  { id: 'juridico',    label: 'Estudios Jurídicos',  tag: 'nicho:juridico' },
  { id: 'gym',         label: 'Gimnasios',           tag: 'nicho:gym' },
  { id: 'inmobiliaria',label: 'Inmobiliarias',       tag: 'nicho:inmobiliaria' },
  { id: 'peluqueria',  label: 'Peluquerías',         tag: 'nicho:peluqueria' },
];

const CHANNELS = [
  { id: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', color: 'text-emerald-400' },
  { id: 'email',    icon: Mail,          label: 'Email',    color: 'text-blue-400' },
];

function CountryToggle({ p, selected, onToggle, count }) {
  return (
    <button
      onClick={() => onToggle(p.code)}
      data-testid={`pais-btn-${p.code}`}
      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all ${
        selected
          ? 'bg-primary/15 border-primary text-primary shadow-[0_0_12px_rgba(0,229,255,0.15)]'
          : 'bg-card border-border-subtle text-text-muted hover:border-primary/30'
      }`}
    >
      <span className="text-2xl leading-none">{p.flag}</span>
      <span className="text-[10px] font-medium whitespace-nowrap">{p.name}</span>
      {count > 0 && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${selected ? 'bg-primary/30' : 'bg-white/10'}`}>
          {count}
        </span>
      )}
      {selected && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-2.5 h-2.5 text-[#0A1A3F]" />
        </span>
      )}
    </button>
  );
}

export default function PaisesCampaignHub({ authFetch }) {
  const [selected, setSelected] = useState(new Set());
  const [nicho, setNicho] = useState('todos');
  const [channel, setChannel] = useState('whatsapp');
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(1); // 1=select, 2=message, 3=confirm, 4=sent
  const [message, setMessage] = useState({ name: '', subject: '', body: '', only_consent: true });
  const [result, setResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStyle, setAiStyle] = useState('visual');

  /* Load segments/contact counts */
  const loadSegments = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/crm/segments`);
      const j = await r.json();
      setSegments(j.segments || []);
    } catch (e) { /* noop */ }
  }, [authFetch]);

  /* Load contacts for preview */
  const loadContacts = useCallback(async () => {
    if (selected.size === 0) { setContacts([]); setTotal(0); return; }
    setLoading(true);
    try {
      const tags = [...selected].map(code => PAISES.find(p => p.code === code)?.tag).filter(Boolean);
      const nichoObj = NICHOS.find(n => n.id === nicho);
      const allTags = [...tags, nichoObj?.tag].filter(Boolean);
      const tagParam = allTags.join(',');
      const r = await authFetch(`${API}/crm/contacts?tags=${encodeURIComponent(tagParam)}&limit=200`);
      const j = await r.json();
      setContacts(j.contacts || []);
      setTotal(j.total || j.contacts?.length || 0);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [authFetch, selected, nicho]);

  useEffect(() => { loadSegments(); }, [loadSegments]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const togglePais = (code) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(PAISES.map(p => p.code)));
  const clearAll = () => setSelected(new Set());

  /* Count per country from segments */
  const countForPais = (pais) => {
    const seg = segments.find(s => s.tag === pais.tag);
    return seg?.count || 0;
  };

  /* Generate AI message */
  const generateAI = async () => {
    setAiLoading(true);
    try {
      const countriesList = [...selected].map(c => PAISES.find(p => p.code === c)?.name).join(', ');
      const nichoLabel = NICHOS.find(n => n.id === nicho)?.label || 'negocios';
      const r = await authFetch(`${API}/crm/campaigns/generate-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nicho: `${nichoLabel} en ${countriesList}`,
          canal: channel,
          objetivo: `captar clientes de ${nichoLabel} en ${countriesList}`,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al generar mensaje');
      // The API returns {"messages": {"visual": {"texto": "..."}, "auditivo": {...}, "kinestesico": {...}}}
      const variant = j.messages?.[aiStyle] || j.messages?.visual || {};
      const text = variant.texto || variant.text || j.message || j.content || '';
      const countriesList2 = [...selected].map(c => PAISES.find(p => p.code === c)?.name).join(', ');
      setMessage(prev => ({
        ...prev,
        body: text,
        name: prev.name || `Campaña ${countriesList2} - ${nichoLabel}`,
        subject: `Solución IA para ${nichoLabel} en ${countriesList2}`,
      }));
      toast.success('Mensaje generado con IA y PNL');
    } catch (e) { toast.error(e.message); }
    setAiLoading(false);
  };

  /* Launch campaign */
  const launch = async () => {
    setSending(true);
    try {
      const tags = [...selected].map(code => PAISES.find(p => p.code === code)?.tag).filter(Boolean);
      const nichoObj = NICHOS.find(n => n.id === nicho);
      const allTags = [...tags, nichoObj?.tag].filter(Boolean);

      // Create campaign
      const campR = await authFetch(`${API}/crm/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: message.name || `Campaña ${[...selected].join('-')}`,
          channel,
          tags: allTags,
          message: message.body,
          subject: message.subject,
          only_consent: message.only_consent,
        }),
      });
      const campJ = await campR.json();
      if (!campR.ok) throw new Error(campJ.detail || 'Error al crear campaña');
      const campId = campJ.campaign_id || campJ.id;

      // Send
      const sendR = await authFetch(`${API}/crm/campaigns/${campId}/send`, { method: 'POST' });
      const sendJ = await sendR.json();
      if (!sendR.ok) throw new Error(sendJ.detail || 'Error al enviar campaña');

      setResult(sendJ);
      setStep(4);
      toast.success('Campaña lanzada correctamente');
    } catch (e) { toast.error(e.message); }
    setSending(false);
  };

  const reset = () => { setStep(1); setSelected(new Set()); setMessage({ name: '', subject: '', body: '', only_consent: true }); setResult(null); };

  const selectedList = [...selected].map(c => PAISES.find(p => p.code === c));

  return (
    <div className="space-y-6" data-testid="paises-campaign-hub">
      {/* Header */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-heading font-bold text-text-heading flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Globe className="w-5 h-5 text-primary" />
          </span>
          Campañas por País
        </h2>
        <p className="text-text-muted text-sm mt-1">Selecciona países y lanza campañas WhatsApp/Email a tus contactos del CRM.</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[
          { n: 1, label: 'Países & Nicho' },
          { n: 2, label: 'Mensaje' },
          { n: 3, label: 'Confirmar' },
          { n: 4, label: 'Enviado' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              step === s.n ? 'bg-primary text-[#0A1A3F]' : step > s.n ? 'bg-emerald-500/15 text-emerald-400' : 'bg-card text-text-muted'
            }`} data-testid={`campaign-step-${s.n}`}>
              {step > s.n ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span>{s.n}</span>}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < arr.length - 1 && <div className="w-6 h-px bg-border-subtle flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Select countries + nicho */}
      {step === 1 && (
        <div className="space-y-5">
          {/* Channel */}
          <div className="glass-card p-5">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-3">Canal de envío</p>
            <div className="flex gap-2">
              {CHANNELS.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => setChannel(ch.id)}
                  data-testid={`channel-btn-${ch.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                    channel === ch.id ? 'bg-primary text-[#0A1A3F]' : 'bg-card border border-border-subtle text-text-muted hover:border-primary/30'
                  }`}
                >
                  <ch.icon className="w-4 h-4" /> {ch.label}
                </button>
              ))}
            </div>
          </div>

          {/* Countries */}
          <div className="glass-card p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider">Selecciona países</p>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-primary hover:underline" data-testid="paises-select-all">Todos</button>
                <span className="text-text-muted">·</span>
                <button onClick={clearAll} className="text-xs text-text-muted hover:text-text-heading" data-testid="paises-clear-all">Ninguno</button>
              </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
              {PAISES.map(p => (
                <CountryToggle
                  key={p.code}
                  p={p}
                  selected={selected.has(p.code)}
                  onToggle={togglePais}
                  count={countForPais(p)}
                />
              ))}
            </div>
          </div>

          {/* Nicho filter */}
          <div className="glass-card p-5">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-3">Filtrar por nicho</p>
            <div className="flex flex-wrap gap-2">
              {NICHOS.map(n => (
                <button
                  key={n.id}
                  onClick={() => setNicho(n.id)}
                  data-testid={`nicho-btn-${n.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    nicho === n.id ? 'bg-primary text-[#0A1A3F]' : 'bg-card border border-border-subtle text-text-muted hover:border-primary/30'
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className={`glass-card p-5 transition-all ${selected.size > 0 ? 'border-primary/30' : ''}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Contactos encontrados</p>
                  <p className="text-3xl font-bold text-primary font-heading mt-1">
                    {loading ? '...' : total}
                  </p>
                </div>
                {selectedList.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {selectedList.map(p => (
                      <span key={p.code} className="text-lg" title={p.name}>{p.flag}</span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={selected.size === 0 || total === 0}
                className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2 disabled:opacity-40"
                data-testid="paises-next-btn"
              >
                Siguiente <span>→</span>
              </button>
            </div>
            {total > 0 && (
              <p className="text-xs text-text-muted mt-2">
                {total} contactos recibirán la campaña por {channel === 'whatsapp' ? 'WhatsApp' : 'Email'}.
                {message.only_consent && ' Solo contactos con consentimiento activo.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: Message */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="text-base font-heading font-semibold text-text-heading">Redactar mensaje</h3>
              <div className="flex items-center gap-2">
                <select
                  value={aiStyle}
                  onChange={e => setAiStyle(e.target.value)}
                  className="input-base text-xs py-1.5 px-2 w-auto"
                >
                  <option value="visual">Visual (imágenes, datos)</option>
                  <option value="auditivo">Auditivo (ritmo, palabras)</option>
                  <option value="kinestesico">Kinestésico (acción, tacto)</option>
                </select>
                <button
                  onClick={generateAI}
                  disabled={aiLoading}
                  className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5"
                  data-testid="generate-ai-btn"
                >
                  <Zap className="w-3.5 h-3.5" />
                  {aiLoading ? 'Generando...' : 'Generar con IA'}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-text-muted mb-1 block">Nombre de la campaña</label>
              <input
                type="text"
                value={message.name}
                onChange={e => setMessage(p => ({ ...p, name: e.target.value }))}
                className="input-base w-full"
                placeholder="ej: Campaña Perú - Dentistas Enero"
                data-testid="camp-name-input"
              />
            </div>

            {channel === 'email' && (
              <div>
                <label className="text-xs text-text-muted mb-1 block">Asunto del email</label>
                <input
                  type="text"
                  value={message.subject}
                  onChange={e => setMessage(p => ({ ...p, subject: e.target.value }))}
                  className="input-base w-full"
                  placeholder="ej: Solución IA para tu clínica dental"
                  data-testid="camp-subject-input"
                />
              </div>
            )}

            <div>
              <label className="text-xs text-text-muted mb-1 block">Mensaje</label>
              <textarea
                value={message.body}
                onChange={e => setMessage(p => ({ ...p, body: e.target.value }))}
                rows={8}
                className="input-base w-full resize-y text-sm"
                placeholder="Escribe aquí tu mensaje o usa el generador IA con PNL/Neuromarketing..."
                data-testid="camp-body-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="only_consent"
                checked={message.only_consent}
                onChange={e => setMessage(p => ({ ...p, only_consent: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="only_consent" className="text-xs text-text-muted">
                Solo enviar a contactos con consentimiento activo (recomendado)
              </label>
            </div>
          </div>

          <div className="flex gap-2 justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary text-sm py-2 px-5">← Volver</button>
            <button
              onClick={() => setStep(3)}
              disabled={!message.body.trim()}
              className="btn-primary text-sm py-2.5 px-6 disabled:opacity-40"
              data-testid="camp-next-step3"
            >
              Revisar y Confirmar →
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Confirm */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="glass-card p-6 space-y-5 border border-primary/20">
            <h3 className="text-base font-heading font-semibold text-text-heading flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" /> Resumen de campaña
            </h3>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Países seleccionados</p>
                <div className="flex flex-wrap gap-1">
                  {selectedList.map(p => (
                    <span key={p.code} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {p.flag} {p.name}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Destinatarios estimados</p>
                <p className="text-2xl font-bold text-primary font-heading">{total} contactos</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Canal</p>
                <p className="text-sm text-text-heading capitalize">{channel}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Solo con consentimiento</p>
                <p className="text-sm text-text-heading">{message.only_consent ? 'Sí' : 'No'}</p>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Vista previa del mensaje</p>
              <div className="bg-[#0A1A3F]/60 rounded-xl p-4 text-sm text-text-body whitespace-pre-wrap border border-border-subtle">
                {message.body}
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-between">
            <button onClick={() => setStep(2)} className="btn-secondary text-sm py-2 px-5">← Editar</button>
            <button
              onClick={launch}
              disabled={sending}
              className="btn-primary text-sm py-2.5 px-8 flex items-center gap-2"
              data-testid="camp-launch-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Enviando...' : `Lanzar campaña a ${total} contactos`}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: Sent */}
      {step === 4 && (
        <div className="glass-card p-10 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="text-xl font-heading font-bold text-text-heading">¡Campaña lanzada!</h3>
          {result && (
            <div className="flex justify-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400 font-heading">{result.sent || 0}</p>
                <p className="text-text-muted text-xs mt-1">Enviados</p>
              </div>
              {result.failed > 0 && (
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-400 font-heading">{result.failed}</p>
                  <p className="text-text-muted text-xs mt-1">Fallidos</p>
                </div>
              )}
            </div>
          )}
          <button onClick={reset} className="btn-primary text-sm py-2.5 px-8" data-testid="camp-reset-btn">
            Nueva campaña
          </button>
        </div>
      )}
    </div>
  );
}
